import React, { useCallback, useMemo, useState } from "react";

import Page from "components/Page";
import TopTabMenu from "components/TopTabMenu";
import FileRibbon from "components/FileManager/FileRibbon";
import FileManagerPanel from "components/FileManager/FileManagerPanel";
import FileManagerModal from "components/FileManager/FileManagerModal";

import { DateInput, DropdownSelect } from "components/InputStyled";
import { useProfile } from "context/ProfileContext";
import { useDialog } from "context/DialogContext";
import { SHARING_OPTIONS, normalizeSharing } from "lib/permissions";

import {
  usePortfoliosFileManager,
  portfoliosOpenView,
} from "./useFileManager";

import Main from "./Main";
import Positions from "./Positions";
import CashFlows from "./CashFlows";
import PdfExportMenu, { PORTFOLIO_PDF_REPORTS } from "./PdfExportMenu";
import { loadPortfolioReportPositions } from "./portfolioReportPositions";
import {
  addYearsIso,
  buildPortfolioCashFlows,
  getLatestScheduledMaturityDate,
} from "./portfolioCashFlows";
import exportPortfolioBundlePdf from "pages/FundPlanner/reports/exportPortfolioBundlePdf";
import {
  PortfoliosProvider,
  usePortfolios,
} from "pages/FundPlanner/context/PortfoliosContext";

const TOP_TABS = [
  { key: "Main", label: "Main" },
  { key: "Positions", label: "Positions" },
  { key: "CashFlows", label: "Cash Flows" },
];

const PDF_SELECTION_KEY = "fundplanner.portfolios.pdfReports.v1";
const DEFAULT_PDF_REPORTS = PORTFOLIO_PDF_REPORTS.map((option) => option.value);

function loadPdfSelection() {
  try {
    const parsed = JSON.parse(sessionStorage.getItem(PDF_SELECTION_KEY) || "null");
    return Array.isArray(parsed)
      ? DEFAULT_PDF_REPORTS.filter((key) => parsed.includes(key))
      : DEFAULT_PDF_REPORTS;
  } catch (_err) {
    return DEFAULT_PDF_REPORTS;
  }
}

function todayIso() {
  const date = new Date();
  const yyyy = date.getFullYear();
  const mm = String(date.getMonth() + 1).padStart(2, "0");
  const dd = String(date.getDate()).padStart(2, "0");
  return `${yyyy}-${mm}-${dd}`;
}

function addDaysIso(dateIso, days) {
  const base = new Date(`${dateIso}T00:00:00`);
  if (Number.isNaN(base.getTime())) return "";
  base.setDate(base.getDate() + days);
  return base.toISOString().slice(0, 10);
}

function latestReportDateIso() {
  return addDaysIso(todayIso(), -1);
}

function monthStartIso(dateIso) {
  const safeDate = dateIso || latestReportDateIso();
  return `${safeDate.slice(0, 8)}01`;
}

function defaultPeriodStartIso(endDateIso) {
  const monthStart = monthStartIso(endDateIso);
  if (monthStart && monthStart < endDateIso) return monthStart;

  const base = new Date(`${endDateIso}T00:00:00`);
  if (Number.isNaN(base.getTime())) return monthStart;
  base.setMonth(base.getMonth() - 1, 1);
  return base.toISOString().slice(0, 10);
}

function PortfoliosInner() {
  const { availableOrgs, selectedOrg, orgMode, selectOrg } = useProfile();
  const { confirm } = useDialog();
  const fm = usePortfoliosFileManager();

  const {
    currentId,
    currentName,
    currentOwner,
    sharing,
    setCurrentName,
    setSharing,
    resetPortfolio,
    loadPortfolio,
    savePortfolio,
    saveAsPortfolio,
    deletePortfolio,
    isDirty,
    isSaving,
    canDelete,
    canChangeSharing,
    isReadOnly,
    lastError,
  } = usePortfolios();

  const [activeTab, setActiveTab] = useState("Main");
  const [showOpenModal, setShowOpenModal] = useState(false);
  const [reportMode, setReportMode] = useState("single");
  const [asOfDate, setAsOfDate] = useState(latestReportDateIso());
  const [periodStartDate, setPeriodStartDate] = useState(() => monthStartIso(latestReportDateIso()));
  const [periodEndDate, setPeriodEndDate] = useState(latestReportDateIso());
  const [generatedReport, setGeneratedReport] = useState(null);
  const [isReportGenerating, setIsReportGenerating] = useState(false);
  const [reportLoadError, setReportLoadError] = useState("");
  const [refreshKey, setRefreshKey] = useState(0);
  const [pdfReports, setPdfReports] = useState(loadPdfSelection);
  const [isPdfGenerating, setIsPdfGenerating] = useState(false);
  const [cashFlowSettings, setCashFlowSettings] = useState({
    projectionEndDate: "",
    bucketMode: "monthly",
    includeScheduledCashFlows: true,
    includeDemandLiquidity: false,
  });

  const showWorkspace = !!(currentId || isDirty);
  const latestReportDate = latestReportDateIso();

  const reportScope = useMemo(
    () => ({
      reportMode,
      asOfDate: reportMode === "period" ? periodEndDate : asOfDate,
      startDate: reportMode === "period" ? periodStartDate : "",
      endDate: reportMode === "period" ? periodEndDate : asOfDate,
      includeAllPortfolioGroups: false,
      includedPortfolioIds: currentId ? [currentId] : [],
    }),
    [asOfDate, currentId, periodEndDate, periodStartDate, reportMode]
  );

  const reportCriteriaKey = JSON.stringify(reportScope);
  const reportRefreshRequired =
    Boolean(generatedReport) && generatedReport.criteriaKey !== reportCriteriaKey;

  const handlePdfReportsChange = useCallback((values) => {
    setPdfReports(values);
    try {
      sessionStorage.setItem(PDF_SELECTION_KEY, JSON.stringify(values));
    } catch (_err) {
      // Session storage may be unavailable; the in-memory selection still works.
    }
  }, []);

  const handlePdfExport = useCallback(async () => {
    if (!pdfReports.length || !selectedOrg?.id || !currentId) return;

    setIsPdfGenerating(true);
    try {
      const sharedArgs = {
        orgId: selectedOrg.id,
        asOfDate: reportScope.asOfDate,
        reportMode: reportScope.reportMode,
        startDate: reportScope.startDate,
        endDate: reportScope.endDate,
        includeAllPortfolioGroups: reportScope.includeAllPortfolioGroups,
        includedPortfolioIds: reportScope.includedPortfolioIds,
      };
      const reportData = await loadPortfolioReportPositions(sharedArgs);
      let cashRows = reportData.rows;

      if (pdfReports.includes("cashFlows") && reportScope.reportMode === "period") {
        const cashData = await loadPortfolioReportPositions({
          ...sharedArgs,
          reportMode: "single",
          startDate: "",
          endDate: reportScope.asOfDate,
        });
        cashRows = cashData.rows;
      }

      const projectionEndDate =
        cashFlowSettings.projectionEndDate ||
        getLatestScheduledMaturityDate(cashRows, reportScope.asOfDate) ||
        addYearsIso(reportScope.asOfDate, 1);
      const cashFlows = buildPortfolioCashFlows({
        rows: cashRows,
        asOfDate: reportScope.asOfDate,
        projectionEndDate,
        bucketMode: cashFlowSettings.bucketMode,
        includeScheduledCashFlows: cashFlowSettings.includeScheduledCashFlows,
        includeDemandLiquidity: false,
      });
      const frequencyLabel =
        cashFlowSettings.bucketMode === "annual"
          ? "Annual"
          : cashFlowSettings.bucketMode === "quarterly"
            ? "Quarterly"
            : "Monthly";

      await exportPortfolioBundlePdf({
        orgId: selectedOrg.id,
        selectedReports: pdfReports,
        reportArgs: {
          rows: reportData.rows,
          funds: reportData.funds,
          positions: reportData.positions,
          asOfDate: reportData.asOfDate || reportScope.asOfDate,
          reportMode: reportScope.reportMode,
          startDate: reportScope.startDate,
          endDate: reportScope.endDate,
          periodAnalysis: reportData.periodAnalysis,
          orgName: selectedOrg.name || "",
          portfolioName: currentName || "",
        },
        cashFlowArgs: {
          cashFlows,
          asOfDate: reportScope.asOfDate,
          projectionEndDate,
          frequencyLabel,
          orgName: selectedOrg.name || "",
          portfolioName: currentName || "",
          scopeType: "portfolio",
          scopeName: currentName || "",
          showFundColumn: true,
        },
      });
    } catch (err) {
      console.warn("FundPlanner portfolio PDF export failed:", err);
      alert(`PDF export failed: ${err?.message || String(err)}`);
    } finally {
      setIsPdfGenerating(false);
    }
  }, [
    cashFlowSettings,
    currentId,
    currentName,
    pdfReports,
    reportScope,
    selectedOrg,
  ]);

  const handleReportModeChange = useCallback((mode) => {
    setReportMode(mode);
    if (mode === "period") {
      const fallbackDate = asOfDate || latestReportDateIso();
      const defaultStartDate = defaultPeriodStartIso(fallbackDate);
      setPeriodEndDate(fallbackDate);
      setPeriodStartDate(defaultStartDate);
    }
  }, [asOfDate]);

  const handleGenerateReport = useCallback(async () => {
    if (!selectedOrg?.id || !currentId) return;
    setIsReportGenerating(true);
    setReportLoadError("");
    try {
      const primaryData = await loadPortfolioReportPositions({
        orgId: selectedOrg.id,
        ...reportScope,
      });
      const cashFlowData = reportScope.reportMode === "period"
        ? await loadPortfolioReportPositions({
            orgId: selectedOrg.id,
            ...reportScope,
            reportMode: "single",
            startDate: "",
            endDate: reportScope.asOfDate,
          })
        : primaryData;
      setGeneratedReport({
        criteriaKey: reportCriteriaKey,
        scope: reportScope,
        primaryData,
        cashFlowData,
      });
    } catch (err) {
      setReportLoadError(err?.message || "Unable to generate portfolio report.");
    } finally {
      setIsReportGenerating(false);
    }
  }, [currentId, reportCriteriaKey, reportScope, selectedOrg?.id]);

  const handlePeriodStartDateChange = useCallback((value) => {
    setPeriodStartDate(value);
  }, []);

  const handlePeriodEndDateChange = useCallback((value) => {
    setPeriodEndDate(value);
  }, []);

  const handleNew = useCallback(() => {
    resetPortfolio();
    setGeneratedReport(null);
    setReportLoadError("");
    setCurrentName("New Portfolio");
    setActiveTab("Main");
  }, [resetPortfolio, setCurrentName]);

  const handleOpen = useCallback(
    async (portfolio) => {
      await loadPortfolio(portfolio.id);
      setGeneratedReport(null);
      setReportLoadError("");
      setActiveTab("Main");
      fm.openRecord?.(portfolio);
      setRefreshKey((n) => n + 1);
    },
    [fm, loadPortfolio]
  );

  const handleSave = useCallback(async () => {
    const result = await savePortfolio();
    if (result?.ok) {
      fm.refresh?.();
      setRefreshKey((n) => n + 1);
    }
    return result;
  }, [fm, savePortfolio]);

  const handleSaveAs = useCallback(async () => {
    const result = await saveAsPortfolio({
      name: `${currentName || "New Portfolio"} Copy`,
    });

    if (result?.ok) {
      fm.refresh?.();
      setGeneratedReport(null);
      setReportLoadError("");
      setActiveTab("Main");
      setRefreshKey((n) => n + 1);
    }

    return result;
  }, [currentName, fm, saveAsPortfolio]);

  const handleDelete = useCallback(async () => {
    if (!currentId) return;

    const confirmed = await confirm(
      `Delete portfolio "${currentName || "Untitled Portfolio"}"? This cannot be undone.`,
      {
        title: "Delete Portfolio",
        okLabel: "Delete",
        cancelLabel: "Cancel",
      }
    );

    if (!confirmed) return;

    const result = await deletePortfolio();

    if (result?.ok) {
      fm.refresh?.();
      setGeneratedReport(null);
      setReportLoadError("");
      setActiveTab("Main");
      setRefreshKey((n) => n + 1);
    }
  }, [confirm, currentId, currentName, deletePortfolio, fm]);

  return (
    <Page
      title="FundPlanner - Portfolios"
      showOrganizationSelector
      ribbon={
        <FileRibbon
          entityName="Portfolio"
          fileManager={{
            availableOrgs,
            selectedOrg,
            orgMode,
            selectOrg,
            newLabel: "New Portfolio",
          }}
          onNew={handleNew}
          onOpenBrowse={showWorkspace ? () => setShowOpenModal(true) : null}
          onSave={isDirty && !isReadOnly ? handleSave : undefined}
          onSaveAs={showWorkspace ? handleSaveAs : undefined}
          onDelete={currentId && canDelete ? handleDelete : undefined}
          currentName={currentName || ""}
          currentOwner={currentOwner}
          animateSaving
          currentStatus={
            isSaving
              ? "saving"
              : currentId
                ? isDirty
                  ? "modified"
                  : "saved"
                : "unsaved"
          }
        >
          {!showWorkspace ? (
            !selectedOrg ? (
              <div className="text-sm text-muted">
                Select an organization above to browse portfolios.
              </div>
            ) : (
              <FileManagerPanel
                entityName="Portfolio"
                adapter={fm}
                view={portfoliosOpenView}
                onSelect={handleOpen}
                showToolbar
                showFooter
                maxHeightClassName="max-h-[55vh]"
              />
            )
          ) : null}
        </FileRibbon>
      }
    >
      {showWorkspace ? (
        <div className="rounded-2xl border border-border bg-app shadow-sm">
          <TopTabMenu
            tabs={TOP_TABS}
            activeTab={activeTab}
            onChange={setActiveTab}
          />

          <div className="rounded-b-2xl border-t border-border p-6 space-y-6">
            <div className="flex flex-wrap items-end gap-4">
              <label className="flex flex-col">
                <span className="mb-1 text-xs font-medium text-muted">
                  Portfolio Name
                </span>
                {activeTab === "Main" ? (
                  <input
                    value={currentName || ""}
                    onChange={(e) => setCurrentName(e.target.value)}
                    disabled={isReadOnly}
                    className="h-9 min-w-[22rem] rounded-md border border-input bg-background px-3 text-sm text-foreground shadow-sm focus:outline-none focus:ring-2 focus:ring-ring"
                    placeholder="Portfolio name"
                  />
                ) : (
                  <input
                    value={currentName || "Untitled Portfolio"}
                    disabled
                    readOnly
                    className="h-9 min-w-[22rem] rounded-md border border-input bg-muted/20 px-3 text-sm text-foreground shadow-sm"
                  />
                )}
              </label>

              {activeTab === "Main" || activeTab === "Positions" ? (
                <div className="flex flex-col">
                  <span className="mb-1 text-xs font-medium text-muted">
                    Report Mode
                  </span>
                  <div className="flex h-8 overflow-hidden rounded-md border border-input bg-background shadow-sm">
                    {[
                      { key: "single", label: "Date" },
                      { key: "period", label: "Period" },
                    ].map((option) => (
                      <button
                        key={option.key}
                        type="button"
                        className={`px-3 text-sm transition-colors ${
                          reportMode === option.key
                            ? "btn-primary"
                            : "text-foreground hover:bg-muted/50"
                        }`}
                        onClick={() => handleReportModeChange(option.key)}
                      >
                        {option.label}
                      </button>
                    ))}
                  </div>
                </div>
              ) : null}

              {(activeTab === "Main" || activeTab === "Positions") &&
              reportMode === "period" ? (
                <>
                  <DateInput
                    label="Start Date"
                    value={periodStartDate}
                    onChange={handlePeriodStartDateChange}
                    maxDate={periodEndDate || latestReportDate}
                    className="min-w-48"
                  />
                  <DateInput
                    label="End Date"
                    value={periodEndDate}
                    onChange={handlePeriodEndDateChange}
                    minDate={periodStartDate || ""}
                    maxDate={latestReportDate}
                    className="min-w-48"
                  />
                </>
              ) : (
                <DateInput
                  label="As of Date"
                  value={asOfDate}
                  onChange={setAsOfDate}
                  maxDate={latestReportDate}
                  className="min-w-48"
                />
              )}

              {activeTab === "Main" || activeTab === "Positions" || activeTab === "CashFlows" ? (
                <button
                  type="button"
                  className="btn btn-primary text-sm"
                  onClick={handleGenerateReport}
                  disabled={
                    isReportGenerating ||
                    !currentId ||
                    !asOfDate ||
                    (reportMode === "period" && (!periodStartDate || !periodEndDate))
                  }
                  title={generatedReport ? "Refresh report" : "Generate report"}
                >
                  {isReportGenerating
                    ? "Generating..."
                    : generatedReport
                      ? "Refresh"
                      : "Generate"}
                </button>
              ) : null}

              {activeTab === "Main" ? (
                <DropdownSelect
                  label="Sharing"
                  value={normalizeSharing(sharing)}
                  onChange={setSharing}
                  options={SHARING_OPTIONS}
                  disabled={!canChangeSharing}
                  className="min-w-56"
                />
              ) : null}

              <PdfExportMenu
                values={pdfReports}
                onChange={handlePdfReportsChange}
                onExport={handlePdfExport}
                disabled={!generatedReport || reportRefreshRequired || !currentId}
                generating={isPdfGenerating}
              />
            </div>

            {lastError ? (
              <div className="text-sm text-red-600">{lastError}</div>
            ) : null}

            {reportLoadError ? (
              <div className="text-sm text-red-600">{reportLoadError}</div>
            ) : null}

            {!generatedReport ? (
              <div className="rounded-lg border border-border p-4 text-sm text-muted">
                Select Generate to display the report.
              </div>
            ) : reportRefreshRequired ? (
              <div className="rounded-lg border border-border p-4 text-sm text-muted">
                Report criteria changed. Select Refresh to apply them.
              </div>
            ) : null}

            {activeTab === "Main" && generatedReport ? (
                <Main
                  reportScope={generatedReport.scope}
                  reportData={generatedReport.primaryData}
                  refreshKey={refreshKey}
                  portfolioName={currentName || ""}
                />
            ) : null}

            {activeTab === "Positions" && generatedReport ? (
                <Positions
                  reportScope={generatedReport.scope}
                  reportData={generatedReport.primaryData}
                  refreshKey={refreshKey}
                  portfolioName={currentName || ""}
                />
            ) : null}

            {activeTab === "CashFlows" && generatedReport ? (
              <CashFlows
                reportScope={generatedReport.scope}
                reportData={generatedReport.cashFlowData}
                portfolioName={currentName || ""}
                settings={cashFlowSettings}
                onSettingsChange={setCashFlowSettings}
              />
            ) : null}
          </div>
        </div>
      ) : null}

      {showOpenModal && selectedOrg ? (
        <FileManagerModal
          entityName="Portfolio"
          adapter={fm}
          view={portfoliosOpenView}
          onClose={() => setShowOpenModal(false)}
          onSelect={async (portfolio) => {
            await handleOpen(portfolio);
            setShowOpenModal(false);
          }}
        />
      ) : null}
    </Page>
  );
}

export default function Portfolios() {
  return (
    <PortfoliosProvider>
      <PortfoliosInner />
    </PortfoliosProvider>
  );
}
